import type { ReactNode } from 'react';
import { SetasMarcadores, temSeta } from '../componentes/figura-marcada';
import { AlertTriangle, Ban, HardHat, Info } from 'lucide-react';
import { fatiar, fatiarEquilibrado, linhasDeTexto, paragrafos } from '../manual-utils';
import type { AlertaModelo } from '../manual-textos';

export interface EntradaSumario {
  nivel: 1 | 2;
  rotulo: string;
}

export interface Bloco {
  id: string;
  peso: number;
  conteudo: ReactNode;
  entrada?: EntradaSumario;
  novaPagina?: boolean;
  /** Redução aplicada quando o bloco, sozinho, é mais alto que a área útil. */
  escala?: number;
  /**
   * Pode ser reduzido um pouco para fechar a folha em vez de descer inteiro
   * para a próxima. Vale para figuras, onde 5% a menos é imperceptível; texto e
   * tabela nunca entram aqui, porque mudariam de corpo entre uma folha e outra.
   */
  encolhivel?: boolean;
}

export const LINHAS_TABELA_POR_PAGINA = 22;

const ICONES_ALERTA = {
  proibido: Ban,
  atencao: AlertTriangle,
  obrigatorio: HardHat,
  informacao: Info,
} as const;

export function blocoCapitulo(numero: number, titulo: string, novaPagina = true, id = `cap-${numero}`): Bloco {
  return {
    id,
    peso: 9,
    novaPagina,
    entrada: { nivel: 1, rotulo: `${numero}. ${titulo}` },
    conteudo: (
      <div className="manual-capitulo">
        <h3>CAPÍTULO {numero}</h3>
        <h4><span>{numero}.</span>{titulo.toUpperCase()}</h4>
      </div>
    ),
  };
}

export function blocoSecao(numero: string, titulo: string): Bloco {
  return {
    id: `sec-${numero}`,
    peso: 4,
    entrada: { nivel: 2, rotulo: `${numero} ${titulo}` },
    conteudo: (
      <h5 className="manual-secao-titulo"><span>{numero}</span>{titulo.toUpperCase()}</h5>
    ),
  };
}

export function blocoTexto(texto: string, id: string): Bloco[] {
  return paragrafos(texto).map((paragrafo, indice) => ({
    id: `${id}-p${indice}`,
    peso: linhasDeTexto(paragrafo) + 1,
    conteudo: <p className="manual-paragrafo">{paragrafo}</p>,
  }));
}

export function blocoLista(itens: string[], id: string, ordenada = false): Bloco[] {
  if (!itens.length) return [];
  const porBloco = 14;
  return fatiar(itens, porBloco).map((parte, indice) => ({
    id: `${id}-l${indice}`,
    peso: parte.reduce((total, item) => total + linhasDeTexto(item, 88), 0) + 1,
    conteudo: ordenada ? (
      <ol className="manual-lista-doc" start={indice * porBloco + 1}>
        {parte.map((item, posicao) => <li key={posicao}>{item}</li>)}
      </ol>
    ) : (
      <ul className="manual-lista-doc">
        {parte.map((item, posicao) => <li key={posicao}>{item}</li>)}
      </ul>
    ),
  }));
}

export interface TabelaConfig {
  id: string;
  legenda?: string;
  colunas: string[];
  linhas: (string | ReactNode)[][];
  variante?: 'dados' | 'riscos' | 'plano';
  larguras?: string[];
  alinharEsquerda?: boolean;
  porPagina?: number;
}

export function blocoTabela({
  id,
  legenda,
  colunas,
  linhas,
  variante = 'dados',
  larguras,
  alinharEsquerda,
  porPagina = LINHAS_TABELA_POR_PAGINA,
}: TabelaConfig): Bloco[] {
  if (!linhas.length) return [];
  const partes = fatiarEquilibrado(linhas, porPagina);
  return partes.map((parte, indice) => ({
    id: `${id}-t${indice}`,
    peso: 3 + parte.length * 1.7 + (legenda ? 2 : 0),
    conteudo: (
      <table className={`manual-doc-table is-${variante} ${alinharEsquerda ? 'is-left' : ''}`}>
        {legenda && (
          <caption>
            {legenda}
            {indice > 0 && <em> (continuação)</em>}
          </caption>
        )}
        <thead>
          <tr>{colunas.map((coluna, posicao) => (
            <th key={posicao} style={larguras?.[posicao] ? { width: larguras[posicao] } : undefined}>{coluna}</th>
          ))}</tr>
        </thead>
        <tbody>
          {parte.map((linha, posicao) => (
            <tr key={posicao}>{linha.map((celula, coluna) => <td key={coluna}>{celula}</td>)}</tr>
          ))}
        </tbody>
      </table>
    ),
  }));
}

export function blocoDefinicoes(itens: { termo: string; definicao: string }[], id: string): Bloco[] {
  if (!itens.length) return [];
  return fatiarEquilibrado(itens, 10).map((parte, indice) => ({
    id: `${id}-d${indice}`,
    peso: parte.reduce((total, item) => total + Math.max(2, linhasDeTexto(item.definicao, 78) + 1), 0) + 1,
    conteudo: (
      <dl className="manual-definicoes">
        {parte.map((item) => (
          <div key={item.termo}>
            <dt>{item.termo}</dt>
            <dd>{item.definicao}</dd>
          </div>
        ))}
      </dl>
    ),
  }));
}

export function blocoAlerta(alerta: AlertaModelo, id: string): Bloco {
  const Icone = ICONES_ALERTA[alerta.tipo];
  return {
    id,
    peso: Math.max(7, linhasDeTexto(alerta.texto, 84) + 5),
    conteudo: (
      <div className={`manual-alerta is-${alerta.tipo}`}>
        <Icone aria-hidden="true" />
        <div>
          <strong>{alerta.titulo}</strong>
          <p>{alerta.texto}</p>
        </div>
      </div>
    ),
  };
}

export function blocoImagem({ id, titulo, imagem, legenda, chamadas, marcadores, altura = 250, colunasChamadas }: {
  id: string;
  titulo?: string;
  imagem: string | null;
  legenda?: string;
  chamadas?: { numero: string; descricao: string }[];
  /** Pontos numerados sobre a imagem, em % da largura/altura; `lx`/`ly` é onde fica o número quando há seta. */
  marcadores?: { numero: string; x: number; y: number; lx?: number; ly?: number }[];
  altura?: number;
  colunasChamadas?: [string, string];
}): Bloco[] {
  const listaChamadas = (chamadas ?? []).filter((chamada) => chamada.numero || chamada.descricao);
  const pontos = marcadores ?? [];
  const alt = titulo ?? legenda ?? 'Imagem da máquina';
  // A borda do quadro (1px de cada lado) e a margem interna entram na altura
  // fixa; a imagem recebe o que sobra para não estourar o quadro.
  const alturaImagem = altura - 2;
  const blocos: Bloco[] = [{
    id: `${id}-img`,
    peso: Math.round(altura / 13) + (legenda ? 2 : 0) + (titulo ? 2 : 0),
    encolhivel: true,
    conteudo: (
      <figure className="manual-figura">
        {titulo && <figcaption className="manual-figura-titulo">{titulo}</figcaption>}
        <div className="manual-figura-area" style={{ height: altura }}>
          {!imagem && <span className="manual-figura-vazia">Imagem não fornecida no levantamento</span>}
          {imagem && !pontos.length && <img src={imagem} alt={alt} />}
          {imagem && pontos.length > 0 && (
            <span className="manual-figura-marcada">
              <img src={imagem} alt={alt} style={{ maxHeight: alturaImagem }} />
              <SetasMarcadores idPrefixo={id} marcadores={pontos} />
              {pontos.map((ponto, indice) => {
                const em = temSeta(ponto) ? { x: ponto.lx as number, y: ponto.ly as number } : ponto;
                return (
                  <span key={indice} className="manual-figura-ponto" style={{ left: `${em.x}%`, top: `${em.y}%` }}>
                    {ponto.numero}
                  </span>
                );
              })}
            </span>
          )}
        </div>
        {legenda && <figcaption className="manual-figura-legenda">{legenda}</figcaption>}
      </figure>
    ),
  }];

  if (listaChamadas.length) {
    blocos.push(...blocoTabela({
      id: `${id}-chamadas`,
      colunas: colunasChamadas ?? ['N°', 'Componente / conjunto'],
      linhas: listaChamadas.map((chamada) => [chamada.numero || '—', chamada.descricao || '—']),
      larguras: ['12%'],
      alinharEsquerda: true,
      porPagina: 18,
    }));
  }
  return blocos;
}

export function blocoNota(texto: string, id: string): Bloco {
  return {
    id,
    peso: linhasDeTexto(texto, 90) + 2,
    conteudo: <p className="manual-nota-doc">{texto}</p>,
  };
}
